"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const caseStudies = [
  {
    industry: "Retail",
    title: "Multi-Site Retail Network Rollout",
    challenge: "A growing retail chain needed standardised network infrastructure across 12 locations with centralised management.",
    outcome: "12 sites deployed in 8 weeks with consistent security policies and centralised monitoring.",
    metric: "12 Sites Deployed",
    services: ["Infrastructure","Network Solutions","Managed IT"],
  },
  {
    industry: "Travel",
    title: "Travel Agency Cloud Migration",
    challenge: "A multi-office travel agency needed to migrate 3 offices to Microsoft 365 and modernise their cloud infrastructure.",
    outcome: "Seamless migration with zero data loss, improved collaboration, and reduced infrastructure costs.",
    metric: "3 Offices Migrated",
    services: ["Cloud & M365","Managed IT","Infrastructure"],
  },
  {
    industry: "Professional Services",
    title: "Legal Firm Security Uplift",
    challenge: "A legal firm needed layered cybersecurity across their practice to protect sensitive client data and meet compliance requirements.",
    outcome: "Comprehensive security framework implemented including endpoint protection, email security, and staff training.",
    metric: "100% Compliance Achieved",
    services: ["Cybersecurity","Managed IT","Cloud & M365"],
  },
  {
    industry: "Construction",
    title: "New Office Infrastructure Deployment",
    challenge: "A growing construction company needed complete technology deployment for their new headquarters.",
    outcome: "Full infrastructure deployment including network, servers, workstations, and communications — delivered on time.",
    metric: "On-Time Delivery",
    services: ["Infrastructure","Network Solutions","ICT Procurement"],
  },
  {
    industry: "Professional Services",
    title: "Managed IT Transition",
    challenge: "A 45-user professional services firm needed to transition from break-fix IT to a proactive managed IT model.",
    outcome: "Reduced downtime by 80%, predictable monthly costs, and improved security posture across the organisation.",
    metric: "80% Less Downtime",
    services: ["Managed IT","Cybersecurity","Cloud & M365"],
  },
  {
    industry: "Healthcare",
    title: "Multi-Site Healthcare Network Redesign",
    challenge: "A healthcare provider with multiple clinics needed a complete network redesign to improve reliability and security.",
    outcome: "Rebuilt network infrastructure with improved uptime, enhanced security controls, and centralised management.",
    metric: "99.9% Uptime Achieved",
    services: ["Network Solutions","Cybersecurity","Managed IT"],
  },
];

const stats = [
  { value: "200+", label: "Sites Deployed" },
  { value: "500+", label: "Businesses Supported" },
  { value: "99.97%", label: "Uptime Delivered" },
  { value: "0", label: "Breaches on Our Watch" },
];

const industryColors: Record<string, string> = {
  Retail: "#3B82F6",
  Travel: "#22D3EE",
  "Professional Services": "#60A5FA",
  Construction: "#F97316",
  Healthcare: "#10B981",
};

export default function CaseStudiesPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Company", href: "/company" },
            { label: "Case Studies" },
          ]}
        />
        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 50%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Case Studies</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Real Results for<br />
                <span className="gradient-text">New Zealand Businesses</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg max-w-3xl mx-auto" style={{ color: "#94A3B8" }}>
                See how Pro IT Solutions has helped businesses deploy infrastructure, improve security, and streamline IT operations.
              </p>
            </FadeInView>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16" style={{ background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6" staggerDelay={0.1} scale={0.92}>
              {stats.map((stat) => (
                <div key={stat.label} className="text-center rounded-card-lg p-4 sm:p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-heading font-800 text-[28px] sm:text-[40px] mb-2 gradient-text">{stat.value}</div>
                  <div className="text-[13px] font-body" style={{ color: "#94A3B8" }}>{stat.label}</div>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Client Success Stories</h2>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {caseStudies.map((cs) => (
                <div key={cs.title} className="rounded-card-lg p-6 flex flex-col transition-all duration-300 hover:scale-[1.02]" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-block px-3 py-1 rounded-pill text-[11px] font-body font-600 whitespace-nowrap" style={{ background: `${industryColors[cs.industry] || "#3B82F6"}20`, color: industryColors[cs.industry] || "#3B82F6", border: `1px solid ${industryColors[cs.industry] || "#3B82F6"}40` }}>{cs.industry}</span>
                    <span className="font-heading font-700 text-[16px]" style={{ color: "#60A5FA" }}>{cs.metric}</span>
                  </div>
                  <h3 className="font-heading font-700 text-[22px] mb-3" style={{ color: "#F8FAFC" }}>{cs.title}</h3>
                  <p className="text-[14px] font-body mb-3 leading-[1.75]" style={{ color: "#94A3B8" }}><strong style={{ color: "#CBD5E1" }}>Challenge:</strong> {cs.challenge}</p>
                  <p className="text-[14px] font-body mb-4 leading-[1.75]" style={{ color: "#94A3B8" }}><strong style={{ color: "#CBD5E1" }}>Outcome:</strong> {cs.outcome}</p>
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {cs.services.map(s => (
                      <span key={s} className="px-2.5 py-1 rounded-lg text-[11px] font-body" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(203,213,225,0.7)" }}>{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to become our next success story?</h2>
                <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>Let&apos;s discuss how Pro IT Solutions can deliver results for your business.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-8 py-3.5 rounded-pill font-heading font-600 btn-primary">Book a Consultation</Link>
                  <Link href="/services" className="px-8 py-3.5 rounded-pill font-heading font-500 btn-secondary">View Our Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
